import {PerformanceRating} from 'src/app/services/applications/application-instance.service';
import {UserExperience} from 'src/app/services/users/users.model';
import {
	ApplicationCategory,
	AuthenticationPrimaryFactor, AuthenticationProtocol, AuthenticationSecondaryFactor,
	AuthenticationType,
	Compliance,
	CriticalityLevel
} from 'src/app/services/tenant/tenant.service';
import {Category} from 'src/app/services/categories/application-category.model';
import {TenantAccount} from 'src/app/services/accounts/account.model';
import {ContractInfo, ContractTypeCost, Cost, Usage} from 'src/app/services/contracts/application-contract.model';
import {QueryRangeType} from 'src/app/services/dashboards/tenant-finance.service';
import {Satisfaction} from 'src/app/services/applications/application-usage.model';
import {Technology} from 'src/app/services/technologies/application-technology.model';
import {Server} from "../servers/server.model";
import {Organization} from "../organizations/organization.service";
import {CatalogTag} from "../tags/catalog-tag.model";
import {ApplicationLifeCycle} from "./application-life-cycle.model";
import {DataApplicationRole} from "../data/data.service";
import {CustomField, CustomFieldFilterForm} from "../custom-fields/custom-field.model";
import {DataAccess} from "../data/data-access.model";

export interface ApplicationInstance {
  applicationId: string;
  tenantId: string;
  catalog: ApplicationCatalog|null;
  name: string;
  logo: string;
  memo: string|null;
  description: string|null;
  hostingType: HostingType;
  criticality: CriticalityLevel|null;
  accessUrl: string|null;
  usageActivated: boolean;
  supportPhone: string|null;
  supportEmail: string|null;
  supportUrl: string|null;
  lastUpdate: Date;
  urlModalDismissed: boolean;
  authenticationType: AuthenticationType|null;
  authenticationPrimaryFactor: AuthenticationPrimaryFactor|null;
  authenticationSecondaryFactor: AuthenticationSecondaryFactor|null;
  authenticationProtocol: AuthenticationProtocol|null;
  desktopApplicationNames: string[];
  displayedInExtension: boolean;
  state: ApplicationState;
  archivedFrom: ArchivedFrom | null;
}

export interface ApplicationUsageUrl {
	id: string;
	domain: string;
	path: string | null;
}

export enum ApplicationState {
  DISCOVERED = 'DISCOVERED',
  VALIDATED = 'VALIDATED',
  TOLERATED = 'TOLERATED',
  REFUSED = 'REFUSED',
  ARCHIVED = 'ARCHIVED'
}

/** The triaged states that make up the live inventory. */
export const INVENTORY_STATES = [ApplicationState.VALIDATED, ApplicationState.TOLERATED, ApplicationState.REFUSED];

export enum ArchivedFrom {
  INVENTORY = 'INVENTORY',
  AUTODISCOVER = 'AUTODISCOVER'
}

export interface ApplicationStateForm {
  newState: ApplicationState;
}


export enum TechnologyEndOfLifeFilter {
  EOL_REACHED = 'EOL_REACHED',
  EOL_WITHIN_SIX_MONTHS = 'EOL_WITHIN_SIX_MONTHS',
  MAINTAINED_BEYOND_SIX_MONTHS = 'MAINTAINED_BEYOND_SIX_MONTHS'
}

export interface ApplicationGeneric {
	id: string;
	name: string;
	logo: string;
	description?: string|null;
	criticality?: CriticalityLevel|null;
	backgroundColor?: string|null;
}

export interface ApplicationElasticTableData {
  applicationId: string;
  usage: Usage;
  health: Health;
}

export interface ApplicationHealth {
  application: ApplicationGeneric;
  health: Health;
}

export interface ServerApplication {
	application: ApplicationCategory;
	usage: Usage;
	teams: Organization[];
}

export interface OrganizationApplication {
	application: ApplicationGeneric;
	health: Health;
	satisfaction: Satisfaction;
}

export interface Health {
  percent: number|null;
  status: HealthStatus;
  queryType: QueryRangeType;
}

export enum HealthStatus {
  GOOD = 'good',
  NEEDS_IMPROVEMENT = 'needs_improvement',
  POOR = 'poor',
  UNKNOWN = 'unknown' // TODO @TAN manage orElse(100.0) through 'unknown' status
}

export interface Notifications {
  discoveredFeaturesCount: number,
  discoveredTeamsCount: number
}

export interface ApplicationTableData {
  instance: ApplicationInstance;
  category: Category|null;
  cost: DataAccess<Cost>;
  health: Health|null;
  usage: Usage|null;
  notifications: Notifications|null;

  // Optional fields for configurable columns
  responsibles?: TenantAccount[];
  appStatus?: ApplicationStatus|null;
  vendor?: Vendor|null;
  documentationPercent?: number|null;
  satisfaction?: Satisfaction|null;
  userExperience?: UserExperience|null;
  performanceLcp?: PerformanceLcp|null;
  customFields?: CustomField[];
  technologies?: Technology[];
}

export interface PerformanceLcp {
  lcpP75: number|null;
  lcpRating: string|null;
}

export interface ApplicationTableDataResponse {
  data: ApplicationTableData[];
  total: number;
}

export interface ApplicationTableFilterForm {
  name?: string|null;
  category?: string[];
  criticality?: CriticalityLevel[];
  organization?: string[];
  functionalCapacity?: string[];
  responsible?: string[];
  hostingType?: HostingType[];
  status?: ApplicationStatus[];
  tag?: string[];
  vendor?: string[];
  data?: string[];
  technology?: string[];
  technologyVersion?: string[];
  technologyEndOfLife?: TechnologyEndOfLifeFilter[];
  deployedDate?: DateFilter;
  authType?: AuthenticationType[];
  authPrimaryFactor?: AuthenticationPrimaryFactor[];
  authSecondaryFactor?: AuthenticationSecondaryFactor[];
  authProtocol?: AuthenticationProtocol[];
  cost?: ApplicationCostFilter;
  customFields?: CustomFieldFilterForm[];
  noValueFilters?: string[];
  sortDirection?: string;
  sortColumn?: string;
  limit?: number;
  offset?: number;
  visibleColumns?: string[];
  states?: ApplicationState[];
  archivedFrom?: ArchivedFrom | null;
}

export interface ApplicationCostFilter {
	condition: 'minimum' | 'maximum' | 'between';
	minimum: number | null;
	maximum: number | null;
}

// Request interface for application export - accepts either IDs or filters
export interface ApplicationExportRequest {
	applicationIds?: string[] | null;
	name?: string | null;
	category?: string[] | null;
	criticality?: string[] | null;
	organization?: string[] | null;
	functionalCapacity?: string[] | null;
	responsible?: string[] | null;
	hostingType?: string[] | null;
	status?: string[] | null;
	tag?: string[] | null;
  vendor?: string[] | null;
  data?: string[] | null;
  technology?: string[] | null;
  technologyVersion?: string[] | null;
  technologyEndOfLife?: string[] | null;
  deployedDate?: DateFilter | null;
	authType?: string[] | null;
	authProtocol?: string[] | null;
	authPrimaryFactor?: string[] | null;
	authSecondaryFactor?: string[] | null;
	cost?: ApplicationCostFilter | null;
	customFields?: CustomFieldFilterForm[] | null;
	noValueFilters?: string[] | null;
	states?: string[] | null;
	archivedFrom?: string | null;
}

export interface DateFilter {
	beforeAfter: 'before' | 'after';
	date: Date | null;
}

export enum ApplicationSortColumn {
  NAME = 'name',
  HEALTH = 'health',
  CATEGORY = 'category',
  CRITICALITY = 'criticality',
  USAGE = 'usage',
  COST = 'cost',
}

export interface ApplicationCatalog {
  catalogId: string;
  categoryId: string|null;
  name: string;
  logo: string;
  type: HostingType|null;
  applicationUrl: string|null;
  vendor: string;
  vendorUrl: string;
  description: string;
  supportPhone: string|null;
  supportEmail: string|null;
  supportUrl: string|null;
  reviewScore: number|null;
}

export enum HostingType {
  SAAS = 'saas',
  HOSTED = 'hosted',
  HOMEMADE = 'homemade'
}

export interface ApplicationOverview {
  instance: ApplicationInstance;
  category: Category|null;
  status: ApplicationStatus|null;
  vendor: Vendor|null
}

export interface Vendor {
	id: string;
	name: string;
	url?: string;
	description?: string;
}

export interface ApplicationResponsible {
  user: TenantAccount;
  role: ResponsibleRole;
}

export enum ResponsibleRole {
  BUSINESS = 'business',
  TECHNICAL = 'technical'
}

export interface ApplicationCreation {
  application: ApplicationCreationForm;
}

export interface ApplicationCreationForm {
  catalogId?: string;
  categoryId?: string;
  name?: string;
  description?: string | null;
  hostingType?: HostingType;
  accessUrl?: string;
  iconUrl?: string|null;
  tileBgColor?: string|null;
  tileFontColor?: string|null;
  autoDiscoverId?: string|null;
  organizationIds?: string[];
  /** Target inventory status — an app created from a status tab stays in that tab. Backend defaults to VALIDATED. */
  state?: ApplicationState;
}

export interface ApplicationSettingGeneralForm {
  name: string;
  description?: string;
  accessUrl?: string;
}

export interface ApplicationSupportForm {
	  supportPhone?: string;
	  supportEmail?: string;
	  supportUrl?: string;
}

export interface ApplicationSettingUsageForm {
  usageActivated: boolean;
  desktopApplicationNames: string[];
}

export interface ApplicationUsageActivatedForm {
	usageActivated: boolean;
}

export interface DesktopApplicationNamesForm {
	desktopApplicationNames: string[];
}

export interface ApplicationUsageUrlForm {
	urls: ApplicationUsageUrlListForm[];
}

export interface ApplicationUsageUrlListForm {
	id: string | null;
	domain: string;
	path: string | null;
}

export interface ApplicationCriticalityForm {
  criticality: CriticalityLevel|null;
}

export interface ApplicationAuthenticationTypeForm {
	authenticationType: AuthenticationType|null;
	authenticationPrimaryFactor: AuthenticationPrimaryFactor|null;
	authenticationSecondaryFactor: AuthenticationSecondaryFactor|null;
	authenticationProtocol: AuthenticationProtocol|null;
}

export interface ApplicationResponsibleForm {
	accountId: string;
	role?: ResponsibleRole;
}

export interface ApplicationVendorForm {
	  vendorId?: string;
}

export interface ApplicationVendorCreateForm {
	name: string;
	url?: string;
	description?: string;
}

export interface ApplicationCategoryApplicationForm {
  categoryId?: string;
}

export interface ApplicationTypeForm {
  type: HostingType;
}

export interface ApplicationOrganizationListForm {
  organizations: ApplicationOrganizationForm[];
}

export interface ApplicationOrganizationForm {
  organizationId: string;
}

export interface ApplicationTagListForm {
  tags: ApplicationTagForm[]
}

export interface ApplicationTagForm {
  tagId: string;
}

export interface ApplicationUsageForm {
	id: string | null;
	domain: string;
	path: string | null;
}

export interface ApplicationUsageListForm {
	urls: ApplicationUsageForm[]
}

export interface ApplicationForComparison {
	instance: ApplicationInstance;
	category: Category|null;
	health: Health;
	performance: PerformanceRating|null;
	usage: Usage;
	satisfaction: Satisfaction;
	cost: Cost;
	complexity: Compliance;
}

export enum ApplicationStatus {
  PHASE_IN = 'phase_in',
  DEPLOYED = 'deployed',
  PHASE_OUT = 'phase_out',
  RETIRED = 'retired'
}

export interface ApplicationExport {
  instance: ApplicationInstance;
  category: Category|null;
  health: Health;
  usage: Usage;
  cost: DataAccess<Cost>;
  servers: DataAccess<Server[]>;
  technologies: Technology[];
  tags: CatalogTag[];
  responsibles: TenantAccount[];
  lifecycle?: ApplicationLifeCycle;
  contracts: DataAccess<ContractInfo[]>;
  contractTypeCosts: DataAccess<ContractTypeCost[]>;
  customFields: CustomField[];
  errorsCount: number;
  performanceLcp: PerformanceLcp|null;
  vendor: Vendor|null;
  status: ApplicationStatus|null;
}

export interface ApplicationOptimization {
  application: ApplicationGeneric;
  archived: boolean;
  contracts: ContractInfo[];
  usage: Usage;
  potentialEarnings: Cost;
}

export interface ApplicationOptimizationStatistic {
  usagePercent: number;
  potentialEarnings: Cost;
}

export interface ApplicationOptimizationForm {
  archived: boolean;
}

export interface ApplicationUsage {
	application: ApplicationCategory;
	usage: Usage;
	roles: DataApplicationRole[];
}

export interface ExtensionAppListForm {
	id: string;
	displayed: boolean;
}
